import React, { useState, useEffect } from 'react';
import { productService } from '../../../services/api/productService';
import { useSession } from '../../../components/session/SessionManager';
import type { UIProductCategory, UIProduct } from '../../../types/product';
import './ProductSelector.css';

// Tooltip component for showing full descriptions
const Tooltip: React.FC<{ content: string; children: React.ReactNode }> = ({ content, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const tooltipWidth = 320; // max-width from CSS
      const tooltipHeight = 60; // estimated height
      
      // Calculate position to show tooltip above the icon
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      let top = rect.top - tooltipHeight - 10;
      
      // Ensure tooltip doesn't go off screen
      if (left < 10) left = 10;
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }
      if (top < 10) {
        top = rect.bottom + 10; // Show below if no space above
      }
      
      setPosition({ top, left });
    }
    setIsVisible(true);
  };

  return (
    <div 
      ref={containerRef}
      className="tooltip-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div 
          className="tooltip-content"
          style={{
            top: position.top,
            left: position.left,
          }}
        >
          {content}
        </div>
      )}
    </div>
  );
};

interface ProductSelectorProps {
  selectedProduct: string;
  selectedInstrument?: string;
  onProductSelect: (productId: string) => void;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({ 
  selectedProduct, 
  selectedInstrument,
  onProductSelect 
}) => {
  const { isAuthenticated, loading: sessionLoading } = useSession();
  const [categories, setCategories] = useState<UIProductCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['directional']));

  // Load product categories when authenticated
  useEffect(() => {
    if (!isAuthenticated || sessionLoading) {
      return;
    }

    loadProductCategories();
  }, [isAuthenticated, sessionLoading]);

  // Update product availability when instrument changes
  useEffect(() => {
    if (selectedInstrument && categories.length > 0) {
      updateProductAvailability(selectedInstrument);
    }
  }, [selectedInstrument, categories.length]);

  const loadProductCategories = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const productCategories = await productService.getUIProductCategories();
      
      // Sort categories by preference and products by popularity
      const sortedCategories = productCategories
        .sort((a, b) => getCategoryPriority(a.id) - getCategoryPriority(b.id))
        .map(category => ({
          ...category,
          products: category.products
            .sort((a, b) => (a.popularityRank || 99) - (b.popularityRank || 99))
            .map(product => ({ ...product, isAvailable: true })) // Ensure all products start as available
        }));

      setCategories(sortedCategories);
    } catch (err) {
      console.error('Failed to load product categories:', err);
      setError('Failed to load product categories. Using default products.');
      
      // Keep the fallback products from service
      const fallbackCategories = await productService.getUIProductCategories();
      setCategories(fallbackCategories);
    } finally {
      setLoading(false);
    }
  };

  const updateProductAvailability = async (instrumentId: string) => {
    if (!instrumentId) return;

    // For now, make all products available since we're using fallback data
    // In production, this would check real product-instrument compatibility
    const updatedCategories = categories.map((category) => ({
      ...category,
      products: category.products.map(product => ({
        ...product,
        isAvailable: true // Default all products to available
      }))
    }));

    setCategories(updatedCategories);
  };

  const getCategoryPriority = (categoryId: string): number => {
    const priorities = {
      'directional': 1,
      'digit-based': 2,
      'growth-based': 3
    };
    return priorities[categoryId as keyof typeof priorities] || 99;
  };

  const getProductIcon = (productType: string): string => {
    const icons = {
      'rise_fall': '↗',
      'higher_lower': '⇅',
      'matches_differs': '⊕',
      'even_odd': '◐',
      'over_under': '⪋',
      'accumulator': '📈'
    };
    return icons[productType as keyof typeof icons] || '○';
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleProductSelect = async (product: UIProduct) => {
    if (!product.isAvailable) return;
    
    // Always call onProductSelect immediately for better UX
    onProductSelect(product.id);
    
    // Load detailed product information in background (optional)
    try {
      await productService.getProductWithDetails(product.type);
      // Could use detailed product data for enhanced UI features
    } catch (error) {
      console.error('Failed to load product details:', error);
    }
  };

  if (sessionLoading) {
    return (
      <aside className="product-selector">
        <div className="products-section">
          <div style={{ padding: '16px', color: '#888', textAlign: 'center' }}>
            Authenticating...
          </div>
        </div>
      </aside>
    );
  }

  if (!isAuthenticated) {
    return (
      <aside className="product-selector">
        <div className="products-section">
          <div style={{ padding: '16px', color: '#888', textAlign: 'center' }}>
            Please log in to view products
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="product-selector">
      <div className="products-section">
        {loading && (
          <div style={{ padding: '16px', color: '#888', textAlign: 'center' }}>
            Loading products...
          </div>
        )}
        
        {error && (
          <div style={{ padding: '16px', color: '#ff9800', fontSize: '12px', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {!loading && categories.map(category => (
          <div key={category.id} className="product-category">
            <h3 
              className={`category-header ${expandedCategories.has(category.id) ? 'expanded' : ''}`}
              onClick={() => toggleCategory(category.id)}
              style={{ 
                cursor: 'pointer', 
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span>{category.displayName}</span>
              <span style={{ fontSize: '12px' }}>
                {expandedCategories.has(category.id) ? '▼' : '▶'}
              </span>
            </h3>
            
            {expandedCategories.has(category.id) && (
              <div className="product-list">
                {category.products.map(product => {
                  const isSelected = selectedProduct === product.id;
                  const isClickable = product.isAvailable;
                  
                  // Truncate description to first 40 characters for one line display
                  const shortDescription = product.description.length > 40 
                    ? product.description.substring(0, 40) + '...' 
                    : product.description;

                  return (
                    <div
                      key={product.id}
                      className={`product-item ${isSelected ? 'active' : ''} ${!isClickable ? 'disabled' : ''}`}
                      onClick={() => isClickable && handleProductSelect(product)}
                      style={{ 
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                        opacity: isClickable ? 1 : 0.6
                      }}
                    >
                      <div className="product-icon">{getProductIcon(product.type)}</div>
                      <div className="product-info">
                        <h4>{product.name}</h4>
                        <div className="product-description-container">
                          <p className="product-description-short">{shortDescription}</p>
                          {product.description.length > 40 && (
                            <Tooltip content={product.description}>
                              <span className="info-icon">?</span>
                            </Tooltip>
                          )}
                        </div>
                        {selectedInstrument && !product.isAvailable && (
                          <span style={{ color: '#ff6b6b', fontSize: '11px' }}>
                            Not available for selected instrument
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        
        {!loading && categories.length === 0 && (
          <div style={{ padding: '16px', color: '#888', textAlign: 'center' }}>
            No products available
          </div>
        )}
      </div>
    </aside>
  );
};

export default ProductSelector;
export { ProductSelector };
export type { UIProduct };
